import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import ConfirmDialog from '../components/ConfirmDialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Plus,
  Trash2,
  Edit2,
  Loader2,
  Users,
  Shield,
  User,
  Mail
} from 'lucide-react';

const AdminPage = () => {
  const { api, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, userId: null });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [statusConfirm, setStatusConfirm] = useState({
    open: false,
    userId: null,
    isActive: null,
    userName: ''
  });
  const [statusLoading, setStatusLoading] = useState(false);
  const [resendingInvite, setResendingInvite] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'member'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'member'
    });
    setDialogOpen(true);
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await api.put(`/users/${editingUser.id}`, {
          name: formData.name,
          role: formData.role
        });
        toast.success('User updated');
      } else {
        const payload = {
          name: formData.name,
          email: formData.email,
          role: formData.role
        };
        if (formData.password) {
          payload.password = formData.password;
        }
        await api.post('/users', payload);
        toast.success(
          formData.password
            ? 'User created.'
            : 'User created. An invitation email will be sent.'
        );
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleDelete = (userId) => {
    if (userId === currentUser?.id) {
      toast.error('Cannot delete your own account');
      return;
    }
    setDeleteConfirm({ open: true, userId });
  };

  const confirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await api.delete(`/users/${deleteConfirm.userId}`);
      toast.success('User deleted');
      setDeleteConfirm({ open: false, userId: null });
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleToggleActive = (user) => {
    if (user.id === currentUser?.id) {
      toast.error('Cannot deactivate your own account');
      return;
    }
    setStatusConfirm({
      open: true,
      userId: user.id,
      isActive: user.is_active,
      userName: user.name
    });
  };

  const confirmToggleActive = async () => {
    if (statusConfirm.userId == null) return;
    const nextIsActive = !statusConfirm.isActive;
    setStatusLoading(true);
    try {
      await api.put(`/users/${statusConfirm.userId}`, { is_active: nextIsActive });
      toast.success(nextIsActive ? 'User activated' : 'User deactivated');
      setStatusConfirm({ open: false, userId: null, isActive: null, userName: '' });
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleResendInvitation = async (userId) => {
    setResendingInvite(userId);
    try {
      await api.post(`/users/${userId}/resend-invitation`);
      toast.success('Invitation email resent');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to resend invitation');
    } finally {
      setResendingInvite(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="admin-loading">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl tracking-tight uppercase">ADMIN</h1>
          <p className="text-muted-foreground text-sm">
            Manage team members
          </p>
        </div>
        <Button onClick={openCreateDialog} data-testid="add-user-btn">
          <Plus className="h-4 w-4 mr-2" />
          ADD USER
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-2xl font-bold">{users.length}</p>
                <p className="text-xs text-muted-foreground uppercase">Total Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-primary" />
              <div>
                <p className="text-2xl font-bold">{users.filter(u => u.role === 'admin').length}</p>
                <p className="text-xs text-muted-foreground uppercase">Admins</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <User className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-2xl font-bold">{users.filter(u => u.role === 'member').length}</p>
                <p className="text-xs text-muted-foreground uppercase">Members</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-5 w-5 rounded-full bg-green-500" />
              <div>
                <p className="text-2xl font-bold">{users.filter(u => u.is_active).length}</p>
                <p className="text-xs text-muted-foreground uppercase">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users List */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground">
            TEAM MEMBERS
          </CardTitle>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="empty-state">
              <Users className="h-12 w-12 text-muted-foreground/40 mb-4" />
              <h3 className="text-lg font-medium mb-1">No team members yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Get started by adding your first team member.
              </p>
              <Button onClick={openCreateDialog}>
                <Plus className="h-4 w-4 mr-2" />
                ADD USER
              </Button>
            </div>
          ) : (
            <>
              {/* Mobile Card Layout */}
              <div className="space-y-3 md:hidden">
                {users.map(user => (
                  <div key={user.id} className={`p-4 border border-border ${!user.is_active ? 'opacity-50' : ''}`} data-testid={`user-row-${user.id}`}>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="min-w-0">
                        <p className="font-medium truncate">{user.name}</p>
                        <p className="text-xs text-muted-foreground font-mono truncate">{user.email}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {user.password_set === false && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleResendInvitation(user.id)}
                            disabled={resendingInvite === user.id}
                            title="Resend Invite"
                            data-testid={`resend-invite-${user.id}`}
                          >
                            {resendingInvite === user.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Mail className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => openEditDialog(user)} data-testid={`edit-user-${user.id}`}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        {user.id !== currentUser?.id && (
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(user.id)} data-testid={`delete-user-${user.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                        {user.role.toUpperCase()}
                      </Badge>
                      <Badge
                        variant={user.is_active ? 'success' : 'destructive'}
                        className="cursor-pointer"
                        onClick={() => handleToggleActive(user)}
                      >
                        {user.is_active ? 'ACTIVE' : 'INACTIVE'}
                      </Badge>
                      {!user.is_active && (
                        <Badge variant="secondary" className="text-[10px]">INACTIVE</Badge>
                      )}
                      {user.password_set === false && (
                        <Badge variant="warning">PENDING INVITE</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {/* Desktop Table Layout */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="table-header">NAME</th>
                      <th className="table-header">EMAIL</th>
                      <th className="table-header">ROLE</th>
                      <th className="table-header">STATUS</th>
                      <th className="table-header">INVITE</th>
                      <th className="table-header text-right">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(user => (
                      <tr key={user.id} className={`table-row ${!user.is_active ? 'opacity-50' : ''}`} data-testid={`user-row-${user.id}`}>
                        <td className="py-3 px-4">
                          <span className="flex items-center gap-2">
                            {user.name}
                            {!user.is_active && (
                              <Badge variant="secondary" className="text-[10px]">INACTIVE</Badge>
                            )}
                          </span>
                        </td>
                        <td className="py-3 px-4 font-mono text-xs">{user.email}</td>
                        <td className="py-3 px-4">
                          <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                            {user.role.toUpperCase()}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <Badge
                            variant={user.is_active ? 'success' : 'destructive'}
                            className="cursor-pointer"
                            onClick={() => handleToggleActive(user)}
                          >
                            {user.is_active ? 'ACTIVE' : 'INACTIVE'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          {user.password_set === false ? (
                            <Badge variant="warning">PENDING INVITE</Badge>
                          ) : null}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {user.password_set === false && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleResendInvitation(user.id)}
                                disabled={resendingInvite === user.id}
                                title="Resend Invite"
                                data-testid={`resend-invite-${user.id}`}
                              >
                                {resendingInvite === user.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Mail className="h-4 w-4" />
                                )}
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditDialog(user)}
                              data-testid={`edit-user-${user.id}`}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            {user.id !== currentUser?.id && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(user.id)}
                                data-testid={`delete-user-${user.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingUser ? 'EDIT USER' : 'ADD USER'}</DialogTitle>
            <DialogDescription>
              {editingUser
                ? 'Update user details below'
                : 'Add a new team member. They will receive a welcome email.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">NAME</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
                data-testid="user-name-input"
              />
            </div>
            {!editingUser && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="email">EMAIL</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    required
                    data-testid="user-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">PASSWORD</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    minLength={8}
                    data-testid="user-password-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Leave password empty to send an invitation email instead.
                  </p>
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label>ROLE</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData(prev => ({ ...prev, role: value }))}
              >
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setDialogOpen(false)}>
                CANCEL
              </Button>
              <Button type="submit" data-testid="user-submit-btn">
                {editingUser ? 'UPDATE' : 'CREATE'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <ConfirmDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => {
          if (!open) setDeleteConfirm({ open: false, userId: null });
        }}
        title="Delete User"
        description="Are you sure you want to delete this user? This action cannot be undone."
        confirmLabel="DELETE"
        onConfirm={confirmDelete}
        variant="destructive"
        loading={deleteLoading}
      />

      <ConfirmDialog
        open={statusConfirm.open}
        onOpenChange={(open) => {
          if (!open) setStatusConfirm({ open: false, userId: null, isActive: null, userName: '' });
        }}
        title={statusConfirm.isActive ? 'Deactivate User' : 'Activate User'}
        description={
          statusConfirm.isActive
            ? `Are you sure you want to deactivate ${statusConfirm.userName || 'this user'}? This user will lose access to the app until reactivated.`
            : `Are you sure you want to activate ${statusConfirm.userName || 'this user'}? This user will be able to access the app again.`
        }
        confirmLabel={statusConfirm.isActive ? 'DEACTIVATE' : 'ACTIVATE'}
        onConfirm={confirmToggleActive}
        variant={statusConfirm.isActive ? 'destructive' : 'default'}
        loading={statusLoading}
      />
    </div>
  );
};

export default AdminPage;
